import { Component, computed, effect, HostListener, inject, input, OnDestroy, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';
import { ComponentInspectorService } from './component-inspector.service';
import { RenderTrackerService } from './render-tracker.service';
import { ComponentNode, ComponentOrigin } from './component-inspector.types';

const STORAGE_KEY_ORIGINS = 'devBar.inspector.enabledOrigins';

const LIFECYCLE_HOOKS = new Set([
  'ngOnInit', 'ngOnDestroy', 'ngOnChanges', 'ngDoCheck',
  'ngAfterContentInit', 'ngAfterContentChecked',
  'ngAfterViewInit', 'ngAfterViewChecked',
]);

interface InspectorProperty {
  key: string;
  displayValue: unknown;
  valueType: string;
  type: 'property' | 'signal' | 'signal-readonly';
}

interface InspectorMethod {
  name: string;
  paramNames: string[];
  paramCount: number;
  isLifecycle: boolean;
}

function displayName(name: string): string {
  return name.startsWith('_') ? name.slice(1) : name;
}

function typeLabel(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

@Component({
  selector: 'app-component-inspector-panel',
  standalone: true,
  imports: [NgTemplateOutlet, MatIconModule, JsonViewerComponent],
  template: `
    <!-- ── Split pane ───────────────────────────────────────────────────── -->
    <div class="split-pane">
      <!-- Left: tree -->
      <div class="tree-pane">
        @if (filteredTree().length === 0) {
          <div class="empty-tree">
            @if (service.scanning()) {
              Escaneando…
            } @else if (service.tree().length === 0) {
              No se encontraron componentes Angular.<br>
              <span class="hint">Asegúrate de que ng.getComponent esté disponible (modo dev).</span>
            } @else {
              Ningún componente coincide con los filtros de origen activos.
            }
          </div>
        } @else {
          <div class="tree-scroll">
            @for (node of filteredTree(); track node.id) {
              <ng-container *ngTemplateOutlet="treeNode; context: { $implicit: node }" />
            }
          </div>
        }
      </div>

      <!-- Right: details -->
      <div class="detail-pane">
        @if (selectedNode()) {
          <!-- ── Header ───────────────────────────────────────────────── -->
          <div class="detail-header">
            <mat-icon class="detail-icon">widgets</mat-icon>
            <span class="detail-name">{{ cleanName(selectedNode()!.name) }}</span>
          </div>

          <!-- ── Properties + Methods (side-by-side when wide enough) ── -->
          <div class="detail-columns">
            <!-- ── Properties ───────────────────────────────────────────── -->
            <div class="detail-section">
              <button class="section-title" (click)="toggleSection('props')">
                <mat-icon class="section-chevron">{{ isSectionOpen('props') ? 'expand_more' : 'chevron_right' }}</mat-icon>
                <mat-icon class="section-icon">tune</mat-icon>
                <span>Propiedades</span>
                <span class="section-count">{{ componentProperties().length }}</span>
              </button>
              @if (isSectionOpen('props')) {
                @if (componentProperties().length === 0) {
                  <div class="section-empty">Sin propiedades</div>
                }
                @for (prop of componentProperties(); track prop.key) {
                  <ng-container *ngTemplateOutlet="propRow; context: {
                    $implicit: prop, path: prop.key, parentObj: null, depth: 0
                  }" />
                }
              }
            </div>

            <!-- ── Methods ──────────────────────────────────────────────── -->
            <div class="detail-section">
              <button class="section-title" (click)="toggleSection('methods')">
                <mat-icon class="section-chevron">{{ isSectionOpen('methods') ? 'expand_more' : 'chevron_right' }}</mat-icon>
                <mat-icon class="section-icon">code</mat-icon>
                <span>Métodos</span>
                <span class="section-count">{{ componentMethods().length }}</span>
              </button>
              @if (isSectionOpen('methods')) {
                @if (componentMethods().length === 0) {
                  <div class="section-empty">Sin métodos</div>
                }
                @for (method of componentMethods(); track method.name) {
                  <div class="method-block">
                    <div class="method-row">
                      @if (method.paramCount === 0) {
                        <button class="method-run-btn" (click)="executeMethod(method.name)" title="Ejecutar">
                          <mat-icon>play_arrow</mat-icon>
                        </button>
                      } @else {
                        <button class="method-run-btn" (click)="toggleMethodExpanded(method.name)"
                                [class.expanded]="isMethodExpanded(method.name)">
                          <mat-icon>{{ isMethodExpanded(method.name) ? 'expand_less' : 'play_arrow' }}</mat-icon>
                        </button>
                      }
                      @if (method.isLifecycle) {
                        <mat-icon class="method-lc-icon" title="Lifecycle hook">sync</mat-icon>
                      }
                      <span class="method-name">{{ method.name }}</span>
                      <span class="method-sig">({{ method.paramNames.join(', ') }})</span>
                    </div>
                    @if (method.paramCount > 0 && isMethodExpanded(method.name)) {
                      <div class="method-params-panel">
                        @for (param of method.paramNames; track $index) {
                          <div class="param-row">
                            <label class="param-label">{{ param }}</label>
                            <input class="param-input"
                                   [value]="getMethodParam(method.name, $index)"
                                   (input)="setMethodParam(method.name, $index, $any($event.target).value)"
                                   (keydown.enter)="executeMethod(method.name)"
                                   placeholder="valor…" />
                          </div>
                        }
                        <button class="method-exec-btn" (click)="executeMethod(method.name)">
                          <mat-icon>play_arrow</mat-icon>
                          <span>Ejecutar</span>
                        </button>
                      </div>
                    }
                  </div>
                }
              }
            </div>
          </div>

          <!-- ── Meta (at the bottom) ─────────────────────────────────── -->
          <div class="detail-section">
            <button class="section-title" (click)="toggleSection('meta')">
              <mat-icon class="section-chevron">{{ isSectionOpen('meta') ? 'expand_more' : 'chevron_right' }}</mat-icon>
              <mat-icon class="section-icon">info_outline</mat-icon>
              <span>Info</span>
            </button>
            @if (isSectionOpen('meta')) {
              <div class="detail-meta">
                <div class="meta-row">
                  <span class="meta-label">Selector</span>
                  <span class="meta-value">{{ selectedNode()!.element.tagName.toLowerCase() }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">Profundidad</span>
                  <span class="meta-value">{{ selectedNode()!.depth }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">Hijos</span>
                  <span class="meta-value">{{ selectedNode()!.children.length }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">Índice</span>
                  <span class="meta-value">#{{ nodeIndex(selectedNode()!.id) }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">Origen</span>
                  <span class="meta-value">{{ selectedNode()!.origin }}</span>
                </div>
              </div>
            }
          </div>
        } @else {
          <div class="empty-detail">
            <mat-icon class="empty-detail-icon">touch_app</mat-icon>
            <span>Selecciona un componente</span>
          </div>
        }
      </div>
    </div>

    <!-- ── Recursive tree node template ─────────────────────────────────── -->
    <ng-template #treeNode let-node>
      <div class="tree-row"
           [style.padding-left.px]="12 + node.depth * 16"
           [class.selected]="selectedId() === node.id"
           [class.highlighted]="isHighlighted(node.id)"
           [class.hot]="isHot(node)"
           (click)="selectNode(node)">
        @if (node.children.length > 0) {
          <button class="tree-toggle" (click)="toggleNode(node.id, $event)">
            <mat-icon>{{ isExpanded(node.id) ? 'expand_more' : 'chevron_right' }}</mat-icon>
          </button>
        } @else {
          <span class="tree-toggle-spacer"></span>
        }
        <mat-icon class="tree-component-icon">{{ node.children.length > 0 ? 'widgets' : 'web_asset' }}</mat-icon>
        <span class="tree-label">{{ cleanName(node.name) }}</span>
        @if (node.children.length > 0) {
          <span class="tree-child-count">{{ node.children.length }}</span>
        }

        @if (isHot(node)) {
          <span class="tree-hot-badge"
                [title]="'CPU: ' + cpuPerSecond(node).toFixed(1) + 'ms/s · ' + updateRate(node) + ' renders/s. Posible función en template, pipe impuro o getter pesado.'">
            <mat-icon>bolt</mat-icon>{{ cpuPerSecond(node).toFixed(1) }}ms/s
          </span>
        }

        <span class="tree-spacer"></span>
        <span class="tree-index">#{{ nodeIndex(node.id) }}</span>

        @if (isPinExpanded(node.id)) {
          <mat-icon class="tree-pin-icon">push_pin</mat-icon>
        }

        <!-- 3-dot context menu -->
        <div class="tree-menu-container" (click)="$event.stopPropagation()">
          <button class="tree-menu-btn" (click)="toggleMenu(node.id)">
            <mat-icon>more_vert</mat-icon>
          </button>
          @if (openMenuId() === node.id) {
            <div class="tree-context-menu">
              <button class="ctx-menu-item" (click)="toggleHighlight(node.id)">
                <mat-icon>{{ isHighlighted(node.id) ? 'visibility_off' : 'highlight' }}</mat-icon>
                <span>{{ isHighlighted(node.id) ? 'Ocultar resaltado' : 'Resaltar' }}</span>
              </button>
              @if (node.children.length > 0) {
                <button class="ctx-menu-item" (click)="highlightTree(node)">
                  <mat-icon>park</mat-icon>
                  <span>Resaltar árbol</span>
                </button>
                <button class="ctx-menu-item" (click)="togglePinExpanded(node.id)">
                  <mat-icon [class.pin-active]="isPinExpanded(node.id)">push_pin</mat-icon>
                  <span>{{ isPinExpanded(node.id) ? 'Desanclar expandido' : 'Anclar expandido' }}</span>
                </button>
              }
            </div>
          }
        </div>
      </div>
      @if (node.children.length > 0 && isExpanded(node.id)) {
        @for (child of node.children; track child.id) {
          <ng-container *ngTemplateOutlet="treeNode; context: { $implicit: child }" />
        }
      }
    </ng-template>

    <!-- ── Recursive property row template ────────────────────────────── -->
    <ng-template #propRow let-prop let-path="path" let-parentObj="parentObj" let-depth="depth">
      <div class="prop-row" [style.padding-left.px]="depth * 12">
        @if (isExpandableValue(prop.displayValue)) {
          <button class="prop-toggle" (click)="togglePropPath(path); $event.stopPropagation()">
            <mat-icon>{{ isPropPathOpen(path) ? 'expand_more' : 'chevron_right' }}</mat-icon>
          </button>
        } @else {
          <span class="prop-toggle-spacer"></span>
        }
        <span class="prop-key">{{ prop.key }}</span>
        @if (prop.type === 'signal') {
          <span class="prop-badge badge-signal">S</span>
        } @else if (prop.type === 'signal-readonly') {
          <span class="prop-badge badge-readonly">R</span>
        }
        <span class="prop-sep">:</span>
        @if (editingPropPath() === path) {
          <input class="prop-input"
                 [value]="editingPropValue()"
                 (input)="editingPropValue.set($any($event.target).value)"
                 (keydown.enter)="savePropertyAt(prop, parentObj)"
                 (keydown.escape)="editingPropPath.set(null)" />
          <button class="prop-action save" (click)="savePropertyAt(prop, parentObj)" title="Guardar">
            <mat-icon>check</mat-icon>
          </button>
          <button class="prop-action cancel" (click)="editingPropPath.set(null)" title="Cancelar">
            <mat-icon>close</mat-icon>
          </button>
        } @else {
          <span class="prop-value" [class]="'val-' + prop.valueType"
                (click)="prop.type !== 'signal-readonly' && startEditPropertyAt(prop, path)">
            {{ formatValue(prop.displayValue) }}
          </span>
        }
        @if (prop.type !== 'signal-readonly' && editingPropPath() !== path) {
          <button class="prop-edit-btn" (click)="startEditPropertyAt(prop, path)" title="Editar">
            <mat-icon>edit</mat-icon>
          </button>
          @if (isExpandableValue(prop.displayValue)) {
            <button class="prop-editor-btn" (click)="openJsonEditor(prop, path, parentObj); $event.stopPropagation()" title="Abrir editor JSON">
              editor
            </button>
          }
        }
      </div>

      @if (isExpandableValue(prop.displayValue) && isJsonEditorOpenAt(path)) {
        <!-- Inline JSON editor (mutually exclusive with tree expansion) -->
        <div class="inline-json-editor">
          <app-json-viewer
            [data]="jsonEditorData()"
            mode="edit"
            [resizable]="true"
            [initialHeight]="200"
            (save)="onJsonEditorSave($event)" />
        </div>
      } @else if (isExpandableValue(prop.displayValue) && isPropPathOpen(path)) {
        <!-- Sub-properties (lazy — only computed when expanded) -->
        @for (subProp of getObjectProperties(prop.displayValue); track subProp.key) {
          <ng-container *ngTemplateOutlet="propRow; context: {
            $implicit: subProp, path: path + '.' + subProp.key, parentObj: prop.displayValue, depth: depth + 1
          }" />
        }
        <!-- Sub-methods -->
        @for (method of getObjectMethods(prop.displayValue); track method.name) {
          <div class="method-block" [style.padding-left.px]="(depth + 1) * 12">
            <div class="method-row">
              @if (method.paramCount === 0) {
                <button class="method-run-btn" (click)="executeObjectMethod(prop.displayValue, method.name, path)" title="Ejecutar">
                  <mat-icon>play_arrow</mat-icon>
                </button>
              } @else {
                <button class="method-run-btn" (click)="toggleMethodExpanded(path + '.' + method.name)"
                        [class.expanded]="isMethodExpanded(path + '.' + method.name)">
                  <mat-icon>{{ isMethodExpanded(path + '.' + method.name) ? 'expand_less' : 'play_arrow' }}</mat-icon>
                </button>
              }
              <span class="method-name">{{ method.name }}</span>
              <span class="method-sig">({{ method.paramNames.join(', ') }})</span>
            </div>
            @if (method.paramCount > 0 && isMethodExpanded(path + '.' + method.name)) {
              <div class="method-params-panel">
                @for (param of method.paramNames; track $index) {
                  <div class="param-row">
                    <label class="param-label">{{ param }}</label>
                    <input class="param-input"
                           [value]="getMethodParam(path + '.' + method.name, $index)"
                           (input)="setMethodParam(path + '.' + method.name, $index, $any($event.target).value)"
                           (keydown.enter)="executeObjectMethod(prop.displayValue, method.name, path)"
                           placeholder="valor…" />
                  </div>
                }
                <button class="method-exec-btn" (click)="executeObjectMethod(prop.displayValue, method.name, path)">
                  <mat-icon>play_arrow</mat-icon>
                  <span>Ejecutar</span>
                </button>
              </div>
            }
          </div>
        }
      }
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
      background: #14142b;
      color: #e5e7eb;
    }

    /* ── Split pane ──────────────────────────────────────────────────────── */

    .split-pane { display: flex; flex: 1; overflow: hidden; }

    .tree-pane {
      flex: 0 0 auto;
      min-width: 100px;
      overflow-y: auto;
      overflow-x: auto;
      border-right: 1px solid #23233a;
      scrollbar-width: thin;
      scrollbar-color: #3a3a5a #0a0a18;
    }
    .tree-pane::-webkit-scrollbar { width: 8px; height: 8px; }
    .tree-pane::-webkit-scrollbar-track { background: #0a0a18; }
    .tree-pane::-webkit-scrollbar-thumb {
      background: #3a3a5a; border-radius: 4px; border: 2px solid #0a0a18;
    }

    .detail-pane {
      flex: 1 1 0;
      min-width: 180px;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 8px;
      scrollbar-width: thin;
      scrollbar-color: #3a3a5a #0a0a18;
      container-type: inline-size;
      container-name: detail;
    }
    .detail-pane::-webkit-scrollbar { width: 8px; }
    .detail-pane::-webkit-scrollbar-track { background: #0a0a18; }
    .detail-pane::-webkit-scrollbar-thumb {
      background: #3a3a5a; border-radius: 4px; border: 2px solid #0a0a18;
    }

    /* ── Tree rows ───────────────────────────────────────────────────────── */

    .tree-row {
      display: flex; align-items: center; gap: 3px;
      padding: 2px 6px 2px 12px;
      font-size: 0.7rem; font-family: ui-monospace, monospace;
      cursor: pointer; min-height: 22px;
      transition: background 0.08s; white-space: nowrap; position: relative;
    }
    .tree-row:hover { background: #1f2937; }
    .tree-row.selected { background: #1e3a5f; }
    .tree-row.highlighted { background: rgba(220, 38, 38, 0.1); }
    .tree-row.selected.highlighted { background: #1e3a5f; }
    .tree-row.hot { box-shadow: inset 2px 0 0 #ef4444; }

    .tree-hot-badge {
      display: inline-flex; align-items: center; gap: 1px;
      font-size: 0.56rem; font-weight: 700; font-family: ui-monospace, monospace;
      color: #fecaca; background: rgba(239, 68, 68, 0.18);
      border: 1px solid rgba(239, 68, 68, 0.5);
      padding: 0 4px 0 2px; border-radius: 3px; flex-shrink: 0;
      line-height: 13px; letter-spacing: 0.02em;
      animation: hotPulse 1.2s ease-in-out infinite;
    }
    .tree-hot-badge mat-icon {
      font-size: 11px; width: 11px; height: 11px; color: #fca5a5;
    }
    @keyframes hotPulse {
      0%, 100% { background: rgba(239, 68, 68, 0.18); }
      50%      { background: rgba(239, 68, 68, 0.38); }
    }

    .tree-toggle {
      background: none; border: none; color: #9ca3af; padding: 0; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      flex-shrink: 0; width: 16px; height: 16px;
    }
    .tree-toggle mat-icon { font-size: 14px; width: 14px; height: 14px; }
    .tree-toggle:hover { color: #fff; }
    .tree-toggle-spacer { width: 16px; flex-shrink: 0; }

    .tree-component-icon {
      font-size: 13px; width: 13px; height: 13px; color: #3b82f6; flex-shrink: 0;
    }
    .tree-label { overflow: hidden; text-overflow: ellipsis; color: #e5e7eb; }
    .tree-row.selected .tree-label { color: #fff; }
    .tree-child-count { font-size: 0.58rem; color: #64748b; margin-left: 2px; flex-shrink: 0; }
    .tree-spacer { flex: 1; min-width: 8px; }
    .tree-index { font-size: 0.54rem; color: #4b5563; flex-shrink: 0; font-family: ui-monospace, monospace; }
    .tree-pin-icon { font-size: 10px; width: 10px; height: 10px; color: #fbbf24; flex-shrink: 0; }

    /* ── 3-dot context menu ──────────────────────────────────────────────── */

    .tree-menu-container { position: relative; flex-shrink: 0; }
    .tree-menu-btn {
      background: none; border: none; color: #4b5563; padding: 0; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      width: 16px; height: 16px; border-radius: 2px;
      opacity: 0; transition: opacity 0.1s, color 0.1s, background 0.1s;
    }
    .tree-row:hover .tree-menu-btn { opacity: 1; }
    .tree-menu-btn:hover { color: #e5e7eb; background: #374151; }
    .tree-menu-btn mat-icon { font-size: 14px; width: 14px; height: 14px; }
    .tree-menu-container:has(.tree-context-menu) .tree-menu-btn { opacity: 1; color: #e5e7eb; }

    .tree-context-menu {
      position: absolute; right: 0; top: calc(100% + 2px); z-index: 200;
      min-width: 180px; background: #1a1a2e; border: 1px solid #3a3a5a;
      border-radius: 6px; padding: 3px 0;
      box-shadow: 0 8px 24px rgba(0,0,0,0.6);
      animation: ctxMenuIn 0.1s ease-out;
    }
    @keyframes ctxMenuIn {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .ctx-menu-item {
      display: flex; align-items: center; gap: 6px; width: 100%;
      padding: 5px 10px; background: none; border: none; color: #e5e7eb;
      font-size: 0.66rem; font-family: ui-monospace, monospace;
      cursor: pointer; transition: background 0.08s; white-space: nowrap;
    }
    .ctx-menu-item:hover { background: #23233a; }
    .ctx-menu-item mat-icon { font-size: 14px; width: 14px; height: 14px; color: #9ca3af; }
    .ctx-menu-item .pin-active { color: #fbbf24; }

    /* ── Detail pane ─────────────────────────────────────────────────────── */

    .detail-header {
      display: flex; align-items: center; gap: 6px;
      padding-bottom: 6px; border-bottom: 1px solid #23233a; margin-bottom: 6px;
    }
    .detail-icon { font-size: 18px; width: 18px; height: 18px; color: #3b82f6; }
    .detail-name {
      font-size: 0.82rem; font-weight: 700; font-family: ui-monospace, monospace;
      color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }

    .detail-meta { display: flex; flex-direction: column; gap: 4px; margin-bottom: 4px; }
    .meta-row {
      display: flex; justify-content: space-between; align-items: center;
      font-size: 0.66rem; font-family: ui-monospace, monospace;
    }
    .meta-label { color: #9ca3af; }
    .meta-value { color: #e5e7eb; font-weight: 600; }

    /* ── Props + Methods side-by-side layout ─────────────────────────── */

    .detail-columns {
      display: flex;
      flex-direction: column;
    }
    @container detail (min-width: 900px) {
      .detail-columns {
        flex-direction: row;
        gap: 12px;
      }
      .detail-columns > .detail-section {
        flex: 1 1 0;
        min-width: 0;
        overflow-x: auto;
        border-top: none;
        padding-top: 0;
        margin-top: 0;
      }
      .detail-columns > .detail-section:first-child {
        border-top: 1px solid #23233a;
        padding-top: 6px;
        margin-top: 6px;
        border-right: 1px solid #23233a;
        padding-right: 12px;
      }
      .detail-columns > .detail-section:last-child {
        border-top: 1px solid #23233a;
        padding-top: 6px;
        margin-top: 6px;
      }
    }

    /* ── Detail sections (props, methods) ─────────────────────────────── */

    .detail-section {
      border-top: 1px solid #23233a;
      padding-top: 6px;
      margin-top: 6px;
    }

    .section-title {
      display: flex; align-items: center; gap: 4px;
      font-size: 0.66rem; font-weight: 700; color: #94a3b8;
      text-transform: uppercase; letter-spacing: 0.04em;
      margin-bottom: 4px;
      background: none; border: none; cursor: pointer;
      padding: 2px 0; width: 100%;
      transition: color 0.1s;
    }
    .section-title:hover { color: #e5e7eb; }
    .section-chevron { font-size: 14px; width: 14px; height: 14px; color: #64748b; flex-shrink: 0; }
    .section-icon { font-size: 13px; width: 13px; height: 13px; color: #64748b; }
    .section-count {
      font-size: 0.58rem; font-weight: 600; color: #4b5563;
      background: #1f2937; padding: 0 4px; border-radius: 3px;
      margin-left: auto;
    }
    .section-empty {
      font-size: 0.64rem; color: #4b5563; font-style: italic; padding: 2px 0 4px;
    }

    /* ── Property rows ───────────────────────────────────────────────────── */

    .prop-row {
      display: flex; align-items: center; gap: 4px;
      padding: 2px 0; min-height: 20px;
      border-bottom: 1px solid #1a1a2e;
    }
    .prop-row:last-child { border-bottom: none; }
    .prop-toggle {
      background: none; border: none; color: #64748b; padding: 0; cursor: pointer;
      display: inline-flex; align-items: center; flex-shrink: 0;
      width: 14px; height: 14px;
    }
    .prop-toggle mat-icon { font-size: 14px; width: 14px; height: 14px; }
    .prop-toggle:hover { color: #e5e7eb; }
    .prop-toggle-spacer { width: 14px; flex-shrink: 0; }
    .prop-key {
      font-size: 0.66rem; font-family: ui-monospace, monospace; color: #93c5fd;
      white-space: nowrap; flex-shrink: 0;
    }
    .prop-badge {
      font-size: 0.5rem; font-weight: 800; padding: 0 3px; border-radius: 2px;
      flex-shrink: 0; line-height: 13px;
    }
    .badge-signal { background: #065f46; color: #34d399; }
    .badge-readonly { background: #3b3b1a; color: #fbbf24; }

    .prop-edit-btn {
      background: none; border: none; color: #4b5563; padding: 0; cursor: pointer;
      display: inline-flex; align-items: center; flex-shrink: 0; margin-left: auto;
      opacity: 0; transition: opacity 0.1s;
    }
    .prop-row:hover .prop-edit-btn { opacity: 1; }
    .prop-edit-btn:hover { color: #e5e7eb; }
    .prop-edit-btn mat-icon { font-size: 12px; width: 12px; height: 12px; }

    .prop-sep {
      color: #4b5563; flex-shrink: 0; font-size: 0.64rem;
    }
    .prop-value {
      font-size: 0.64rem; font-family: ui-monospace, monospace;
      color: #9ca3af; cursor: pointer;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      min-width: 0;
    }
    .prop-value:hover { color: #e5e7eb; }
    .val-string { color: #a5d6a7; }
    .val-number { color: #90caf9; }
    .val-boolean { color: #ce93d8; }
    .val-null, .val-undefined { color: #616161; font-style: italic; }
    .val-array, .val-object { color: #ffcc80; }

    .prop-input {
      flex: 1; min-width: 40px;
      background: #0f0f1e; border: 1px solid #3b82f6; color: #e5e7eb;
      font-family: ui-monospace, monospace; font-size: 0.64rem;
      padding: 1px 4px; border-radius: 3px; outline: none;
    }
    .prop-action {
      background: none; border: none; padding: 1px; cursor: pointer;
      display: inline-flex; align-items: center; border-radius: 2px;
    }
    .prop-action mat-icon { font-size: 13px; width: 13px; height: 13px; }
    .prop-action.save { color: #34d399; }
    .prop-action.save:hover { background: #065f46; }
    .prop-action.cancel { color: #f87171; }
    .prop-action.cancel:hover { background: #7f1d1d; }

    /* ── Method rows ─────────────────────────────────────────────────────── */

    .method-block {
      border-bottom: 1px solid #1a1a2e;
    }
    .method-block:last-child { border-bottom: none; }

    .method-row {
      display: flex; align-items: center; gap: 3px; padding: 3px 0; min-height: 20px;
    }
    .method-lc-icon {
      font-size: 11px; width: 11px; height: 11px; color: #fbbf24; flex-shrink: 0;
    }
    .method-name {
      font-size: 0.66rem; font-family: ui-monospace, monospace; color: #fbbf24;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .method-sig {
      font-size: 0.6rem; font-family: ui-monospace, monospace; color: #4b5563;
      flex-shrink: 0;
    }
    .method-run-btn {
      background: none; border: none; color: #34d399; padding: 1px; cursor: pointer;
      display: inline-flex; align-items: center; border-radius: 2px;
      flex-shrink: 0; transition: color 0.1s, background 0.1s;
    }
    .method-run-btn:hover { background: #065f46; color: #6ee7b7; }
    .method-run-btn.expanded { color: #fbbf24; }
    .method-run-btn mat-icon { font-size: 14px; width: 14px; height: 14px; }

    .method-params-panel {
      padding: 3px 0 4px 16px;
      border-left: 2px solid #23233a;
      margin-left: 6px;
    }
    .param-row {
      display: flex; align-items: center; gap: 4px; margin-bottom: 3px;
    }
    .param-label {
      font-size: 0.62rem; font-family: ui-monospace, monospace; color: #9ca3af;
      min-width: 40px; flex-shrink: 0;
    }
    .param-input {
      flex: 1; min-width: 0;
      background: #0f0f1e; border: 1px solid #3a3a5a; color: #e5e7eb;
      font-family: ui-monospace, monospace; font-size: 0.64rem;
      padding: 2px 5px; border-radius: 3px; outline: none;
    }
    .param-input:focus { border-color: #3b82f6; }
    .method-exec-btn {
      display: inline-flex; align-items: center; gap: 3px;
      background: #065f46; border: none; color: #34d399; padding: 2px 8px;
      border-radius: 3px; cursor: pointer; font-size: 0.62rem;
      font-family: ui-monospace, monospace; font-weight: 600;
      margin-top: 2px; transition: background 0.1s;
    }
    .method-exec-btn:hover { background: #047857; }
    .method-exec-btn mat-icon { font-size: 12px; width: 12px; height: 12px; }

    /* ── Empty states ────────────────────────────────────────────────────── */

    .empty-tree {
      padding: 20px 12px; font-size: 0.74rem; color: #6b7280;
      text-align: center; font-style: italic;
    }
    .hint { font-size: 0.64rem; color: #4b5563; }

    .empty-detail {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; height: 100%; gap: 8px;
      color: #6b7280; font-size: 0.74rem; font-style: italic;
    }
    .empty-detail-icon { font-size: 28px; width: 28px; height: 28px; color: #374151; }

    /* ── Editor button (inline, next to pencil) ──────────────────────── */

    .prop-editor-btn {
      background: #1e3a5f; border: 1px solid #3b82f6; color: #93c5fd;
      padding: 0 5px; border-radius: 3px; cursor: pointer;
      font-size: 0.56rem; font-family: ui-monospace, monospace; font-weight: 700;
      line-height: 15px; white-space: nowrap; flex-shrink: 0;
      opacity: 0; transition: opacity 0.1s, background 0.1s;
      text-transform: uppercase; letter-spacing: 0.03em;
    }
    .prop-row:hover .prop-editor-btn { opacity: 1; }
    .prop-editor-btn:hover { background: #2563eb; color: #fff; }

    /* ── Inline JSON editor ─────────────────────────────────────────── */

    .inline-json-editor {
      border-left: 2px solid #3b82f6;
      margin: 2px 0 4px;
    }

    /* When the JSON viewer inside the inspector is maximized, it needs to
       escape the detail-pane's overflow clipping. We let the :host act as
       the containing block instead. */
    :host {
      position: relative;
    }
    :host ::ng-deep app-json-viewer.jv-maximized {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10001;
    }
  `],
})
export class ComponentInspectorPanelComponent implements OnDestroy {
  readonly service = inject(ComponentInspectorService);
  readonly renderTracker = inject(RenderTrackerService);

  readonly active = input(false);

  // ── Tree state ────────────────────────────────────────────────────────────
  readonly expandedIds = signal(new Set<string>());
  readonly selectedId = signal<string | null>(null);
  readonly highlightedIds = signal(new Set<string>());
  readonly pinnedExpandedIds = signal(new Set<string>());
  readonly openMenuId = signal<string | null>(null);

  // ── Property editing state ────────────────────────────────────────────────
  readonly editingPropPath = signal<string | null>(null);
  readonly editingPropValue = signal('');

  /** Expanded property paths for nested object inspection (lazy to avoid circular refs). */
  readonly expandedPropPaths = signal(new Set<string>());

  // ── Method execution state ────────────────────────────────────────────────
  readonly expandedMethods = signal(new Set<string>());
  readonly methodParamValues = signal(new Map<string, string[]>());

  /** Bumped after property set / method call to force recomputation. */
  readonly refreshTrigger = signal(0);

  /** Collapsible detail sections — props and methods open by default, meta closed. */
  readonly openSections = signal(new Set<string>(['props', 'methods']));

  // ── Inline JSON editor state ─────────────────────────────────────────────
  readonly jsonEditorPath = signal<string | null>(null);
  readonly jsonEditorData = signal<unknown>(null);
  private jsonEditorProp: InspectorProperty | null = null;
  private jsonEditorParentObj: any = null;

  // ── Computed ──────────────────────────────────────────────────────────────

  readonly selectedNode = computed(() => {
    const id = this.selectedId();
    if (!id) return null;
    return this.findNode(this.filteredTree(), id);
  });

  readonly totalCount = computed(() => {
    return ComponentInspectorService.allIds(this.filteredTree()).length;
  });

  readonly nodeIndexMap = computed(() => {
    const map = new Map<string, number>();
    let index = 0;
    const walk = (nodes: ComponentNode[]) => {
      for (const node of nodes) {
        map.set(node.id, index++);
        walk(node.children);
      }
    };
    walk(this.filteredTree());
    return map;
  });

  /** Properties of the selected component (regular + signals). */
  readonly componentProperties = computed((): InspectorProperty[] => {
    this.refreshTrigger();
    const node = this.selectedNode();
    if (!node) return [];
    const cmp = this.getComponentInstance(node);
    if (!cmp) return [];

    const props: InspectorProperty[] = [];
    for (const key of Object.keys(cmp)) {
      if (key.startsWith('__') || key.startsWith('ɵ')) continue;
      try {
        const raw = cmp[key];
        if (typeof raw === 'function') {
          if (this.isWritableSignal(raw)) {
            try { props.push({ key, displayValue: raw(), valueType: typeLabel(raw()), type: 'signal' }); }
            catch { props.push({ key, displayValue: '[error]', valueType: 'error', type: 'signal' }); }
          } else if (this.isSignalLike(raw)) {
            try { props.push({ key, displayValue: raw(), valueType: typeLabel(raw()), type: 'signal-readonly' }); }
            catch { props.push({ key, displayValue: '[error]', valueType: 'error', type: 'signal-readonly' }); }
          }
          // Other own-property functions go to methods
          continue;
        }
        props.push({ key, displayValue: raw, valueType: typeLabel(raw), type: 'property' });
      } catch {
        props.push({ key, displayValue: '[error]', valueType: 'error', type: 'property' });
      }
    }
    return props;
  });

  /** Methods of the selected component (prototype + arrow-function class fields). */
  readonly componentMethods = computed((): InspectorMethod[] => {
    const node = this.selectedNode();
    if (!node) return [];
    const cmp = this.getComponentInstance(node);
    if (!cmp) return [];

    const methods: InspectorMethod[] = [];
    const seen = new Set<string>();

    // Own-property functions that aren't signals (arrow methods, bound functions)
    for (const key of Object.keys(cmp)) {
      if (key.startsWith('__') || key.startsWith('ɵ')) continue;
      try {
        const raw = cmp[key];
        if (typeof raw !== 'function') continue;
        if (this.isWritableSignal(raw) || this.isSignalLike(raw)) continue;
        seen.add(key);
        methods.push({
          name: key,
          paramNames: this.extractParamNames(raw),
          paramCount: raw.length,
          isLifecycle: LIFECYCLE_HOOKS.has(key),
        });
      } catch { /* skip */ }
    }

    // Prototype methods
    let proto = Object.getPrototypeOf(cmp);
    while (proto && proto !== Object.prototype) {
      for (const name of Object.getOwnPropertyNames(proto)) {
        if (seen.has(name) || name === 'constructor') continue;
        if (name.startsWith('__') || name.startsWith('ɵ')) continue;
        seen.add(name);
        try {
          const desc = Object.getOwnPropertyDescriptor(proto, name);
          if (desc && typeof desc.value === 'function') {
            methods.push({
              name,
              paramNames: this.extractParamNames(desc.value),
              paramCount: desc.value.length,
              isLifecycle: LIFECYCLE_HOOKS.has(name),
            });
          }
        } catch { /* skip */ }
      }
      proto = Object.getPrototypeOf(proto);
    }

    // Sort: lifecycle first, then alphabetical
    return methods.sort((a, b) => {
      if (a.isLifecycle && !b.isLifecycle) return -1;
      if (!a.isLifecycle && b.isLifecycle) return 1;
      return a.name.localeCompare(b.name);
    });
  });

  // ── Origin filtering ──────────────────────────────────────────────────────

  readonly enabledOrigins = signal<Set<ComponentOrigin>>(this.loadEnabledOrigins());

  readonly availableOrigins = computed(() => {
    const origins = new Set<ComponentOrigin>();
    const walk = (nodes: ComponentNode[]) => {
      for (const n of nodes) { origins.add(n.origin); walk(n.children); }
    };
    walk(this.service.tree());
    return [...origins].sort((a, b) => {
      if (a === 'app') return -1;
      if (b === 'app') return 1;
      return a.localeCompare(b);
    });
  });

  readonly filteredTree = computed(() => {
    const enabled = this.enabledOrigins();
    if (enabled.size === 0) return [];
    return this.filterByOrigin(this.service.tree(), enabled);
  });

  toggleOrigin(origin: ComponentOrigin): void {
    this.enabledOrigins.update(set => {
      const next = new Set(set);
      if (next.has(origin)) next.delete(origin); else next.add(origin);
      return next;
    });
    this.persistEnabledOrigins();
  }

  isOriginEnabled(origin: ComponentOrigin): boolean {
    return this.enabledOrigins().has(origin);
  }

  private filterByOrigin(nodes: ComponentNode[], enabled: Set<ComponentOrigin>, depth = 0): ComponentNode[] {
    const result: ComponentNode[] = [];
    for (const node of nodes) {
      if (enabled.has(node.origin)) {
        result.push({ ...node, depth, children: this.filterByOrigin(node.children, enabled, depth + 1) });
      } else {
        result.push(...this.filterByOrigin(node.children, enabled, depth));
      }
    }
    return result;
  }

  private loadEnabledOrigins(): Set<ComponentOrigin> {
    const raw = localStorage.getItem(STORAGE_KEY_ORIGINS);
    if (!raw) return new Set(['app']);
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return new Set(parsed);
      return new Set(['app']);
    } catch { return new Set(['app']); }
  }

  private persistEnabledOrigins(): void {
    try { localStorage.setItem(STORAGE_KEY_ORIGINS, JSON.stringify([...this.enabledOrigins()])); }
    catch { /* quota */ }
  }

  // ── Overlay state ─────────────────────────────────────────────────────────

  private activeOverlays = new Map<string, { element: HTMLElement; originalBoxShadow: string; originalOutline: string; label: HTMLElement }>();
  private overlayContainer: HTMLElement | null = null;
  private trackingActive = false;
  private readonly scrollHandler = () => this.repositionAllLabels();

  constructor() {
    effect(() => {
      const isActive = this.active();
      // Defer setActive (which triggers a synchronous scan) so the tree
      // signal updates AFTER the current CD cycle, avoiding NG0100 in the
      // parent template that reads inspectorComponentCount().
      setTimeout(() => {
        this.service.setActive(isActive);
        if (isActive && this.expandedIds().size === 0) {
          this.smartCollapse();
        }
      });
      if (!isActive) {
        setTimeout(() => this.clearAllOverlays());
      }
    });

    effect(() => {
      const highlighted = this.highlightedIds();
      const selected = this.selectedId();
      const tree = this.filteredTree();
      const indices = this.nodeIndexMap();
      setTimeout(() => this.refreshOverlays(highlighted, selected, tree, indices));
    });
  }

  ngOnDestroy(): void {
    this.service.setActive(false);
    this.clearAllOverlays();
  }

  // ── Display helpers ───────────────────────────────────────────────────────

  cleanName(name: string): string { return displayName(name); }

  nodeIndex(id: string): number { return this.nodeIndexMap().get(id) ?? 0; }

  private rateCache = new Map<string, { tick: number; rate: number; cpu: number; hot: boolean }>();

  private readRates(node: ComponentNode): { rate: number; cpu: number; hot: boolean } {
    const tick = this.renderTracker.tick();
    const cached = this.rateCache.get(node.id);
    if (cached && cached.tick === tick) return cached;
    const cmp = this.getComponentInstance(node);
    const entry = {
      tick,
      rate: this.renderTracker.updateRate(cmp),
      cpu: this.renderTracker.cpuPerSecond(cmp),
      hot: this.renderTracker.isHot(cmp),
    };
    this.rateCache.set(node.id, entry);
    return entry;
  }

  updateRate(node: ComponentNode): number { return this.readRates(node).rate; }
  cpuPerSecond(node: ComponentNode): number { return this.readRates(node).cpu; }
  isHot(node: ComponentNode): boolean { return this.readRates(node).hot; }

  formatValue(value: unknown): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') {
      const display = value.length > 50 ? value.slice(0, 50) + '…' : value;
      return `"${display}"`;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) return `Array(${value.length})`;
    if (typeof value === 'object') {
      try {
        const str = JSON.stringify(value);
        return str.length > 60 ? str.slice(0, 60) + '…' : str;
      } catch { return '{…}'; }
    }
    return String(value);
  }

  // ── Expand / collapse ──────────────────────────────────────────────────────

  toggleNode(id: string, event: Event): void {
    event.stopPropagation();
    this.expandedIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  isExpanded(id: string): boolean { return this.expandedIds().has(id); }

  expandAll(): void {
    const ids: string[] = [];
    const walk = (nodes: ComponentNode[]) => {
      for (const n of nodes) { if (n.children.length > 0) ids.push(n.id); walk(n.children); }
    };
    walk(this.filteredTree());
    this.expandedIds.set(new Set(ids));
  }

  collapseAll(): void {
    this.expandedIds.set(new Set());
  }

  smartCollapse(): void {
    const ids = new Set<string>();
    const walk = (nodes: ComponentNode[]) => {
      for (const node of nodes) {
        if (node.children.length > 0) {
          const shouldCollapse = node.children.length >= 5 && ComponentInspectorService.subtreeDepth(node) <= 2;
          if (!shouldCollapse) ids.add(node.id);
        }
        walk(node.children);
      }
    };
    walk(this.filteredTree());
    for (const id of this.pinnedExpandedIds()) ids.add(id);
    this.expandedIds.set(ids);
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  selectNode(node: ComponentNode): void {
    this.selectedId.set(node.id);
    this.editingPropPath.set(null);
    this.expandedMethods.set(new Set());
    this.expandedPropPaths.set(new Set());
  }

  // ── Highlight ──────────────────────────────────────────────────────────────

  isHighlighted(id: string): boolean { return this.highlightedIds().has(id); }

  toggleHighlight(id: string): void {
    this.highlightedIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    this.openMenuId.set(null);
  }

  /** Highlight a node and all its descendants. */
  highlightTree(node: ComponentNode): void {
    const ids: string[] = [];
    const collect = (n: ComponentNode) => {
      ids.push(n.id);
      for (const child of n.children) collect(child);
    };
    collect(node);
    this.highlightedIds.update(set => {
      const next = new Set(set);
      for (const id of ids) next.add(id);
      return next;
    });
    this.openMenuId.set(null);
  }

  highlightAll(): void {
    this.highlightedIds.set(new Set(ComponentInspectorService.allIds(this.filteredTree())));
  }

  hideAllHighlights(): void { this.highlightedIds.set(new Set()); }

  hasHighlights(): boolean { return this.highlightedIds().size > 0; }

  // ── Pin expanded ───────────────────────────────────────────────────────────

  isPinExpanded(id: string): boolean { return this.pinnedExpandedIds().has(id); }

  togglePinExpanded(id: string): void {
    this.pinnedExpandedIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        this.expandedIds.update(exp => { const n = new Set(exp); n.add(id); return n; });
      }
      return next;
    });
    this.openMenuId.set(null);
  }

  // ── Context menu ───────────────────────────────────────────────────────────

  toggleMenu(id: string): void {
    this.openMenuId.set(this.openMenuId() === id ? null : id);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.openMenuId()) this.openMenuId.set(null);
  }

  // ── Section collapse ────────────────────────────────────────────────────────

  toggleSection(section: string): void {
    this.openSections.update(set => {
      const next = new Set(set);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  }

  isSectionOpen(section: string): boolean {
    return this.openSections().has(section);
  }

  // ── Property path expansion (lazy object inspection) ────────────────────────

  togglePropPath(path: string): void {
    // If expanding and editor is open at this path, close the editor
    if (!this.expandedPropPaths().has(path) && this.jsonEditorPath() === path) {
      this.closeJsonEditor();
    }
    this.expandedPropPaths.update(set => {
      const next = new Set(set);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }

  isPropPathOpen(path: string): boolean {
    return this.expandedPropPaths().has(path);
  }

  isExpandableValue(value: unknown): boolean {
    return value !== null && value !== undefined && typeof value === 'object';
  }

  /** Introspect an arbitrary object's own properties (lazy — called only when expanded). */
  getObjectProperties(obj: any): InspectorProperty[] {
    if (!obj || typeof obj !== 'object') return [];
    // Read refreshTrigger so template re-evaluates after mutations
    this.refreshTrigger();
    const props: InspectorProperty[] = [];
    for (const key of Object.keys(obj)) {
      if (key.startsWith('__') || key.startsWith('ɵ')) continue;
      try {
        const raw = obj[key];
        if (typeof raw === 'function') {
          if (this.isWritableSignal(raw)) {
            try { props.push({ key, displayValue: raw(), valueType: typeLabel(raw()), type: 'signal' }); }
            catch { props.push({ key, displayValue: '[error]', valueType: 'error', type: 'signal' }); }
          } else if (this.isSignalLike(raw)) {
            try { props.push({ key, displayValue: raw(), valueType: typeLabel(raw()), type: 'signal-readonly' }); }
            catch { props.push({ key, displayValue: '[error]', valueType: 'error', type: 'signal-readonly' }); }
          }
          continue;
        }
        props.push({ key, displayValue: raw, valueType: typeLabel(raw), type: 'property' });
      } catch {
        props.push({ key, displayValue: '[error]', valueType: 'error', type: 'property' });
      }
    }
    return props;
  }

  /** Introspect an arbitrary object's methods (lazy). */
  getObjectMethods(obj: any): InspectorMethod[] {
    if (!obj || typeof obj !== 'object') return [];
    const methods: InspectorMethod[] = [];
    const seen = new Set<string>();

    for (const key of Object.keys(obj)) {
      if (key.startsWith('__') || key.startsWith('ɵ')) continue;
      try {
        const raw = obj[key];
        if (typeof raw !== 'function') continue;
        if (this.isWritableSignal(raw) || this.isSignalLike(raw)) continue;
        seen.add(key);
        methods.push({ name: key, paramNames: this.extractParamNames(raw), paramCount: raw.length, isLifecycle: false });
      } catch {}
    }

    let proto = Object.getPrototypeOf(obj);
    while (proto && proto !== Object.prototype) {
      for (const name of Object.getOwnPropertyNames(proto)) {
        if (seen.has(name) || name === 'constructor') continue;
        if (name.startsWith('__') || name.startsWith('ɵ')) continue;
        seen.add(name);
        try {
          const desc = Object.getOwnPropertyDescriptor(proto, name);
          if (desc && typeof desc.value === 'function') {
            methods.push({ name, paramNames: this.extractParamNames(desc.value), paramCount: desc.value.length, isLifecycle: false });
          }
        } catch {}
      }
      proto = Object.getPrototypeOf(proto);
    }

    return methods.sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Execute a method on a nested object. */
  executeObjectMethod(obj: any, methodName: string, parentPath: string): void {
    if (!obj || typeof obj[methodName] !== 'function') return;

    const methods = this.getObjectMethods(obj);
    const method = methods.find(m => m.name === methodName);
    const paramKey = parentPath + '.' + methodName;
    let args: unknown[] = [];
    if (method && method.paramCount > 0) {
      const rawArgs = this.methodParamValues().get(paramKey) ?? [];
      args = method.paramNames.map((_, i) => this.parseInputValue(rawArgs[i] ?? ''));
    }

    try {
      const result = obj[methodName](...args);
      console.log(`[Inspector] ${parentPath}.${methodName}(${args.map(a => JSON.stringify(a)).join(', ')}) →`, result);
    } catch (err) {
      console.error(`[Inspector] ${parentPath}.${methodName}() error:`, err);
    }

    const node = this.selectedNode();
    if (node) {
      const cmp = this.getComponentInstance(node);
      if (cmp) this.triggerChangeDetection(cmp);
    }
    this.refreshTrigger.update(n => n + 1);
  }

  // ── Property editing ───────────────────────────────────────────────────────

  startEditPropertyAt(prop: InspectorProperty, path: string): void {
    if (prop.type === 'signal-readonly') return;
    this.editingPropPath.set(path);
    const val = prop.displayValue;
    if (typeof val === 'string') {
      this.editingPropValue.set(val);
    } else {
      try { this.editingPropValue.set(JSON.stringify(this.safeClone(val))); }
      catch { this.editingPropValue.set(String(val)); }
    }
  }

  /** Save a property value. parentObj=null means root component. */
  savePropertyAt(prop: InspectorProperty, parentObj: any | null): void {
    const node = this.selectedNode();
    if (!node) return;
    const cmp = this.getComponentInstance(node);
    if (!cmp) return;

    const target = parentObj ?? cmp;
    const parsed = this.parseInputValue(this.editingPropValue());

    try {
      if (prop.type === 'signal') {
        target[prop.key].set(parsed);
      } else {
        target[prop.key] = parsed;
      }
    } catch (err) {
      console.error(`[Inspector] Error setting ${prop.key}:`, err);
    }

    this.triggerChangeDetection(cmp);
    this.editingPropPath.set(null);
    this.refreshTrigger.update(n => n + 1);
  }

  // ── Inline JSON editor ─────────────────────────────────────────────────────

  isJsonEditorOpenAt(path: string): boolean {
    return this.jsonEditorPath() === path;
  }

  openJsonEditor(prop: InspectorProperty, path: string, parentObj: any): void {
    // If already open at this path, toggle off
    if (this.jsonEditorPath() === path) {
      this.closeJsonEditor();
      return;
    }
    this.jsonEditorProp = prop;
    this.jsonEditorParentObj = parentObj;
    // Collapse the tree expansion for this path (editor replaces it)
    this.expandedPropPaths.update(set => { const next = new Set(set); next.delete(path); return next; });
    // Safe-clone with circular ref + depth protection
    this.jsonEditorData.set(this.safeClone(prop.displayValue));
    this.jsonEditorPath.set(path);
  }

  closeJsonEditor(): void {
    this.jsonEditorPath.set(null);
    this.jsonEditorProp = null;
    this.jsonEditorParentObj = null;
  }

  onJsonEditorSave(value: unknown): void {
    const prop = this.jsonEditorProp;
    if (!prop) return;
    const node = this.selectedNode();
    if (!node) return;
    const cmp = this.getComponentInstance(node);
    if (!cmp) return;

    const target = this.jsonEditorParentObj ?? cmp;
    try {
      if (prop.type === 'signal') {
        target[prop.key].set(value);
      } else {
        target[prop.key] = value;
      }
    } catch (err) {
      console.error(`[Inspector] Error setting ${prop.key}:`, err);
    }

    this.triggerChangeDetection(cmp);
    this.refreshTrigger.update(n => n + 1);
    this.closeJsonEditor();
  }

  // ── Method execution ───────────────────────────────────────────────────────

  toggleMethodExpanded(name: string): void {
    this.expandedMethods.update(set => {
      const next = new Set(set);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  isMethodExpanded(name: string): boolean { return this.expandedMethods().has(name); }

  getMethodParam(methodName: string, index: number): string {
    return this.methodParamValues().get(methodName)?.[index] ?? '';
  }

  setMethodParam(methodName: string, index: number, value: string): void {
    this.methodParamValues.update(map => {
      const next = new Map(map);
      const params = [...(next.get(methodName) ?? [])];
      params[index] = value;
      next.set(methodName, params);
      return next;
    });
  }

  executeMethod(name: string): void {
    const node = this.selectedNode();
    if (!node) return;
    const cmp = this.getComponentInstance(node);
    if (!cmp || typeof cmp[name] !== 'function') return;

    const method = this.componentMethods().find(m => m.name === name);
    let args: unknown[] = [];
    if (method && method.paramCount > 0) {
      const rawArgs = this.methodParamValues().get(name) ?? [];
      args = method.paramNames.map((_, i) => this.parseInputValue(rawArgs[i] ?? ''));
    }

    try {
      const result = cmp[name](...args);
      console.log(`[Inspector] ${name}(${args.map(a => JSON.stringify(a)).join(', ')}) →`, result);
    } catch (err) {
      console.error(`[Inspector] ${name}() error:`, err);
    }

    this.triggerChangeDetection(cmp);
    this.refreshTrigger.update(n => n + 1);
  }

  // ── Overlay management ─────────────────────────────────────────────────────

  private refreshOverlays(
    highlighted: Set<string>, selectedId: string | null,
    tree: ComponentNode[], indices: Map<string, number>,
  ): void {
    this.clearAllOverlays();
    for (const id of highlighted) {
      if (id === selectedId) continue;
      const node = this.findNode(tree, id);
      if (node && document.body.contains(node.element)) {
        this.applyOverlay(node, 'red', indices.get(id) ?? 0);
      }
    }
    if (selectedId) {
      const node = this.findNode(tree, selectedId);
      if (node && document.body.contains(node.element)) {
        this.applyOverlay(node, 'blue', indices.get(selectedId) ?? 0);
      }
    }
    if (this.activeOverlays.size > 0) this.startPositionTracking();
    else this.stopPositionTracking();
  }

  private applyOverlay(node: ComponentNode, color: 'red' | 'blue', index: number): void {
    const el = node.element;
    const c = color === 'blue' ? '#3b82f6' : '#dc2626';
    const bg = color === 'blue' ? 'rgba(59, 130, 246, 0.92)' : 'rgba(220, 38, 38, 0.92)';

    const origBoxShadow = el.style.boxShadow;
    const origOutline = el.style.outline;
    el.style.boxShadow = `inset 0 0 0 1px ${c}, 0 0 0 1px ${c}`;
    el.style.outline = `1px solid ${c}`;
    el.style.outlineOffset = '-1px';

    const container = this.ensureOverlayContainer();
    const label = document.createElement('div');
    label.style.cssText =
      `position:fixed;font-size:10px;font-family:ui-monospace,monospace;padding:1px 6px;` +
      `border-radius:2px 2px 0 0;white-space:nowrap;pointer-events:none;color:#fff;font-weight:600;` +
      `letter-spacing:0.02em;background:${bg};z-index:99999;line-height:14px;`;
    label.textContent = `${displayName(node.name)} #${index}`;
    container.appendChild(label);
    this.positionLabel(label, el);

    this.activeOverlays.set(node.id, { element: el, originalBoxShadow: origBoxShadow, originalOutline: origOutline, label });
  }

  private clearAllOverlays(): void {
    for (const [, ov] of this.activeOverlays) {
      ov.element.style.boxShadow = ov.originalBoxShadow;
      ov.element.style.outline = ov.originalOutline;
      ov.element.style.outlineOffset = '';
      ov.label.remove();
    }
    this.activeOverlays.clear();
    this.stopPositionTracking();
    this.destroyOverlayContainer();
  }

  private ensureOverlayContainer(): HTMLElement {
    if (!this.overlayContainer) {
      this.overlayContainer = document.createElement('div');
      this.overlayContainer.style.cssText =
        'position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;z-index:99999;' +
        // Override UA popover defaults so the container stays invisible/passive.
        'inset:0;margin:0;padding:0;border:none;background:transparent;overflow:visible;';
      this.overlayContainer.setAttribute('data-inspector-overlays', '');
      this.overlayContainer.setAttribute('popover', 'manual');
      document.body.appendChild(this.overlayContainer);
      try { this.overlayContainer.showPopover(); } catch {}
      this.startOverlayTopLayerWatch();
    }
    return this.overlayContainer;
  }

  private destroyOverlayContainer(): void {
    this.stopOverlayTopLayerWatch();
    if (this.overlayContainer) {
      try { this.overlayContainer.hidePopover(); } catch {}
      this.overlayContainer.remove();
      this.overlayContainer = null;
    }
  }

  /** Re-show the overlay container in the top layer whenever CDK opens a new
   *  overlay (dialog/menu), so the inspector labels stay above it. */
  private overlayTopLayerObserver: MutationObserver | null = null;
  private overlayBodyObserver: MutationObserver | null = null;

  private startOverlayTopLayerWatch(): void {
    const container = this.overlayContainer;
    if (!container) return;

    const reShow = () => {
      setTimeout(() => {
        try { container.hidePopover(); container.showPopover(); } catch {}
      });
    };

    const cdkContainer = document.querySelector('.cdk-overlay-container');
    if (cdkContainer) {
      this.overlayTopLayerObserver = new MutationObserver(reShow);
      this.overlayTopLayerObserver.observe(cdkContainer, { childList: true });
    } else {
      this.overlayBodyObserver = new MutationObserver(() => {
        const c = document.querySelector('.cdk-overlay-container');
        if (c) {
          this.overlayBodyObserver!.disconnect();
          this.overlayBodyObserver = null;
          this.overlayTopLayerObserver = new MutationObserver(reShow);
          this.overlayTopLayerObserver.observe(c, { childList: true });
        }
      });
      this.overlayBodyObserver.observe(document.body, { childList: true });
    }
  }

  private stopOverlayTopLayerWatch(): void {
    this.overlayTopLayerObserver?.disconnect();
    this.overlayTopLayerObserver = null;
    this.overlayBodyObserver?.disconnect();
    this.overlayBodyObserver = null;
  }

  private positionLabel(label: HTMLElement, target: HTMLElement): void {
    const rect = target.getBoundingClientRect();
    label.style.left = `${rect.left}px`;
    label.style.top = `${Math.max(0, rect.top - 16)}px`;
  }

  private repositionAllLabels(): void {
    for (const [, ov] of this.activeOverlays) {
      if (document.body.contains(ov.element)) this.positionLabel(ov.label, ov.element);
    }
  }

  private startPositionTracking(): void {
    if (this.trackingActive) return;
    this.trackingActive = true;
    window.addEventListener('scroll', this.scrollHandler, true);
    window.addEventListener('resize', this.scrollHandler);
  }

  private stopPositionTracking(): void {
    if (!this.trackingActive) return;
    this.trackingActive = false;
    window.removeEventListener('scroll', this.scrollHandler, true);
    window.removeEventListener('resize', this.scrollHandler);
  }

  // ── Internal helpers ──────────────────────────────────────────────────────

  private getComponentInstance(node: ComponentNode): any {
    const ng = (window as any).ng;
    return ng?.getComponent?.(node.element) ?? null;
  }

  private triggerChangeDetection(cmp: any): void {
    const ng = (window as any).ng;
    if (ng?.applyChanges) {
      try { ng.applyChanges(cmp); } catch { /* ignore */ }
    }
  }

  private isWritableSignal(fn: unknown): boolean {
    return typeof fn === 'function'
      && typeof (fn as any).set === 'function'
      && typeof (fn as any).update === 'function';
  }

  private isSignalLike(fn: unknown): boolean {
    if (typeof fn !== 'function') return false;
    try {
      const symbols = Object.getOwnPropertySymbols(fn);
      return symbols.some(s => s.toString().includes('SIGNAL'));
    } catch { return false; }
  }

  private extractParamNames(fn: Function): string[] {
    try {
      const str = fn.toString();
      const match = str.match(/^(?:async\s+)?(?:function\s*\w*\s*)?\(([^)]*)\)/);
      if (!match) {
        const arrow = str.match(/^(?:async\s+)?(\w+)\s*=>/);
        if (arrow) return [arrow[1]];
        return Array.from({ length: fn.length }, (_, i) => `arg${i}`);
      }
      const params = match[1].trim();
      if (!params) return [];
      return params.split(',').map(p => p.trim().replace(/\s*=\s*.*$/, '').replace(/:.+$/, '').trim()).filter(Boolean);
    } catch {
      return Array.from({ length: fn.length }, (_, i) => `arg${i}`);
    }
  }

  private parseInputValue(input: string): unknown {
    const trimmed = input.trim();
    if (trimmed === '') return undefined;
    if (trimmed === 'null') return null;
    if (trimmed === 'undefined') return undefined;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    try { return JSON.parse(trimmed); }
    catch { return trimmed; }
  }

  /**
   * Deep-clone a value safely, handling circular references and capping
   * depth / total node count so that enormous or self-referencing objects
   * (e.g. Angular service instances with injected deps) never freeze the page.
   */
  private safeClone(value: unknown, maxDepth = 30, maxNodes = 5000): unknown {
    const seen = new WeakSet<object>();
    let nodeCount = 0;
    const clone = (v: unknown, depth: number): unknown => {
      if (v === null || v === undefined) return v;
      if (typeof v !== 'object') return v;          // primitives
      if (typeof (v as any).toJSON === 'function') {
        try { return (v as any).toJSON(); } catch { /* ignore */ }
      }
      if (depth > maxDepth || nodeCount > maxNodes) return '[Truncated]';
      if (seen.has(v as object)) return '[Circular]';
      seen.add(v as object);
      nodeCount++;
      if (Array.isArray(v)) {
        const arr = v.map(item => clone(item, depth + 1));
        seen.delete(v as object);
        return arr;
      }
      const result: Record<string, unknown> = {};
      for (const key of Object.keys(v as object)) {
        if (key.startsWith('__') || key.startsWith('ɵ')) continue;
        try { result[key] = clone((v as any)[key], depth + 1); }
        catch { result[key] = '[Error]'; }
      }
      seen.delete(v as object);
      return result;
    };
    return clone(value, 0);
  }

  private findNode(nodes: ComponentNode[], id: string): ComponentNode | null {
    for (const node of nodes) {
      if (node.id === id) return node;
      const found = this.findNode(node.children, id);
      if (found) return found;
    }
    return null;
  }
}
